import {Observable, combineLatest, EMPTY} from 'rxjs';
import {filter, flatMap} from 'rxjs/operators';

import {type Action} from './redux/action-types';
import {hasValue} from './typescript';

type ActionsArray<Payload> = Array<Action<Payload>>;
type Actions<Payload> = Action<Payload> | ActionsArray<Payload>;
export const isActionsArray = (actions: Actions<any>): actions is ActionsArray<any> => actions instanceof Array;
export const isSingleAction = (actions: Actions<any>): actions is Action<any> =>
    Object.prototype.hasOwnProperty.call(actions, 'type');

/**
 * Will replay the latest action emitted by the replayObservable when the triggerReplayObservable fires any action
 */
export const replayOnTriggerObservable = <ReplayAction extends Actions<any>, TriggerPayload>(
    replayObservable: Observable<ReplayAction>,
    resetObservable: Observable<any>,
    triggerReplayObservable: Observable<TriggerPayload>,
    predicate: (replayAction: ReplayAction, triggerValue: TriggerPayload) => boolean,
    updateReplayAction?: (
        replayAction: Action<any>,
        triggerValue: TriggerPayload | null,
    ) => Action<any> | Array<Action<any>>,
): Observable<Action<any>> => {
    const filteredReplayObservable = replayObservable.pipe(
        filter(action => {
            if (isActionsArray(action)) {
                return hasValue(action[0]) && !action[0]._triggeredReloadEvent;
            }
            if (isSingleAction(action)) {
                return hasValue(action) && !action._triggeredReloadEvent;
            }
            return false;
        }),
    );

    // Because we're using combineLatest for the returned observable and because we expect the returned observable
    // to be used in an epic that lives for the entire lifetime of the app instance, we have to have a way of "flushing"
    // the pipeline once in a while. That's what's happening here.
    const emptyResetObservable = resetObservable ? resetObservable.map(() => null) : Observable.empty();

    // The replay observable and the trigger observable are made flushable
    const resetableReplayObservable = Observable.merge(filteredReplayObservable, emptyResetObservable);
    const resetableTriggerReplayObservable = Observable.merge(triggerReplayObservable, emptyResetObservable);

    return combineLatest(resetableReplayObservable, resetableTriggerReplayObservable).pipe(
        filter(([replayActions, triggerValue]) => {
            if (hasValue(replayActions) && hasValue(triggerValue)) {
                return (
                    typeof predicate === 'undefined' ||
                    (typeof predicate === 'function' && predicate(replayActions, triggerValue))
                );
            } else {
                return false;
            }
        }),
        flatMap(([replayActions, triggerValue]) => {
            if (!hasValue(replayActions)) {
                return EMPTY;
            }
            const getUpdatedReplayAction = (action: Action<any>) => {
                const updatedAction =
                    typeof updateReplayAction === 'function' ? updateReplayAction(action, triggerValue) : action;

                if (isActionsArray(updatedAction)) {
                    const firstAction = updatedAction[0];

                    if (!firstAction) {
                        return [];
                    }

                    updatedAction[0]._triggeredReloadEvent = true;
                    return updatedAction;
                } else {
                    updatedAction._triggeredReloadEvent = true;
                    return [updatedAction];
                }
            };

            if (isActionsArray(replayActions)) {
                return replayActions.flatMap(getUpdatedReplayAction);
            }
            if (isSingleAction(replayActions)) {
                return getUpdatedReplayAction(replayActions);
            }
            return EMPTY;
        }),
    );
};
